// ABOUTME: Pure, main-thread builder that turns the pivot payload + report view settings into fully
// ABOUTME: resolved sheet specs. Reproduces the on-screen per-mode math so the Excel export is WYSIWYG.
import { getPivotView, pivotSettings, pivotTrendVariants } from "../reportUtils";

// Numeric coercion matching the components' `Number(v) || 0` (non-finite -> 0).
const num = (v) => {
    const n = Number(v);
    return isFinite(n) ? n : 0;
};
const round1 = (n) => Math.round(n * 10) / 10;
const sum = (arr) => arr.reduce((s, v) => s + num(v), 0);
const mean = (arr) => (arr.length ? sum(arr) / arr.length : 0);

// First-column header = the pivot's row dimension, falling back to a generic "name".
const rowDimLabel = (currentReport, t) => (currentReport?.table?.row ? t(currentReport.table.row) : t("name"));

// Row label as shown on screen: translated name, else translated description.
const rowLabel = (row, t) => t(row.name) || t(row.description) || "";

// Trend Δ% cell for the lastYear/previous columns (backend-provided delta). Mirrors RhTrendPills:
// finite delta -> the number; no delta but a positive total -> "new"; otherwise blank.
const trendDeltaCell = (delta, total, t) => {
    if (delta !== null && delta !== undefined && isFinite(delta)) return round1(delta);
    if (num(total) > 0) return t("new");
    return null;
};

// "Current period" trend cell (RhDeltaPill): change across the visible window, first -> second-to-last
// (the last bucket is in-progress). Null when there aren't enough buckets or the baseline is zero.
const acrossTrendCell = (values) => {
    if (!values || values.length < 3) return null;
    const first = Number(values[0]);
    const last = Number(values[values.length - 2]);
    if (!isFinite(first) || !isFinite(last) || first === 0) return null;
    return round1(((last - first) / first) * 100);
};

// Stable sort of mover objects by a numeric key, nulls always sinking to the bottom.
const sortMovers = (movers, key, dir) => {
    const mult = dir === "asc" ? 1 : -1;
    return [...movers].sort((a, b) => {
        const av = a[key];
        const bv = b[key];
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return (av - bv) * mult;
    });
};

// NORMAL mode: label + optional trend Δ% columns + every data column + Total, with a column-totals footer.
export const buildNormalSheet = (data, currentReport, t) => {
    const view = getPivotView(currentReport);
    const trends = view.trends; // canonical order: lastYear, previous, current
    const columns = data?.columns || [];
    const rows = data?.rows || [];

    const trendHeader = {
        [pivotTrendVariants.LAST_YEAR]: t("vs_last_year"),
        [pivotTrendVariants.PREVIOUS]: t("vs_prev_period"),
        [pivotTrendVariants.CURRENT]: t("current_period"),
    };

    const header = [rowDimLabel(currentReport, t), ...trends.map((v) => trendHeader[v]), ...columns, t("total")];

    const trendCells = (row) =>
        trends.map((v) => {
            if (v === pivotTrendVariants.LAST_YEAR) return trendDeltaCell(row.comparison?.lastYear?.delta, row.total, t);
            if (v === pivotTrendVariants.PREVIOUS) return trendDeltaCell(row.comparison?.previous?.delta, row.total, t);
            return acrossTrendCell(row.values); // CURRENT
        });

    const body = rows.map((row) => [rowLabel(row, t), ...trendCells(row), ...(row.values || []), row.total ?? null]);

    const footer = [t("total"), ...trends.map(() => null), ...(data?.columnTotals || []), data?.grandTotal ?? null];

    // Trend columns sit at indices 1..trends.length; they hold Δ% values.
    const percentCols = trends.map((_, i) => i + 1);

    return {
        title: t("pivot_mode_normal"),
        columns: header,
        rows: body,
        footer,
        freeze: { row: 1, col: 1 },
        percentCols,
        boldLastCol: true,
    };
};

// PERIOD-OVER-PERIOD: first half vs second half of the window, aggregated by periodAgg (mean or sum).
export const buildPeriodSheet = (data, currentReport, t) => {
    const columns = data?.columns || [];
    const rows = data?.rows || [];
    const half = Math.floor(columns.length / 2);

    const agg = currentReport?.pivot?.periodAgg ?? "average";
    const aggFn = agg === "total" ? sum : mean;

    const firstLabel = columns.length ? `${columns[0]} – ${columns[Math.max(0, half - 1)]}` : "";
    const secondLabel = columns.length ? `${columns[half]} – ${columns[columns.length - 1]}` : "";

    const sortState = currentReport?.pivot?.periodSort ?? { key: "delta", dir: "desc" };

    const movers = rows.map((row) => {
        const values = row.values || [];
        const base = aggFn(values.slice(0, half));
        const firstAvg = round1(base);
        const secondAvg = round1(aggFn(values.slice(half)));
        const delta = base > 0 ? round1(((aggFn(values.slice(half)) - base) / base) * 100) : null;
        return { label: rowLabel(row, t), firstAvg, secondAvg, delta };
    });

    const sorted = sortMovers(movers, sortState.key, sortState.dir);

    const colTotals = data?.columnTotals || [];
    const base = aggFn(colTotals.slice(0, half));
    const totalsDelta = base > 0 ? round1(((aggFn(colTotals.slice(half)) - base) / base) * 100) : null;

    return {
        title: t("pivot_mode_period"),
        columns: [rowDimLabel(currentReport, t), firstLabel, secondLabel, "Δ%"],
        rows: sorted.map((m) => [m.label, m.firstAvg, m.secondAvg, m.delta]),
        footer: [t("total"), round1(base), round1(aggFn(colTotals.slice(half))), totalsDelta],
        freeze: { row: 1, col: 1 },
        percentCols: [3],
        boldLastCol: false,
    };
};

// COMPARE: current-window total vs a backend comparison total (previous period or last year) per row.
// compareAgg=average divides both sides by the bucket count; Δ% is backend-provided and agg-invariant.
export const buildCompareSheet = (data, currentReport, source, compareLabel, t) => {
    const rows = data?.rows || [];
    const agg = currentReport?.pivot?.compareAgg ?? "total";
    const bucketCount = rows?.[0]?.values?.length || 1;
    const sortState = currentReport?.pivot?.compareSort ?? { key: "delta", dir: "desc" };

    const movers = rows.map((row) => {
        const cmp = row.comparison?.[source];
        const cur = row.total ?? 0;
        const cmpTotal = cmp?.total ?? null;
        return {
            label: rowLabel(row, t),
            current: agg === "total" ? cur : round1(cur / bucketCount),
            compare: cmpTotal === null ? null : agg === "total" ? cmpTotal : round1(cmpTotal / bucketCount),
            delta: cmp?.delta ?? null,
        };
    });

    const sorted = sortMovers(movers, sortState.key, sortState.dir);

    let current = 0;
    let compare = 0;
    let hasCompare = false;
    for (const m of sorted) {
        current += m.current || 0;
        if (m.compare !== null) {
            compare += m.compare;
            hasCompare = true;
        }
    }
    const totalsDelta = hasCompare && compare > 0 ? round1(((current - compare) / compare) * 100) : null;

    return {
        title: source === "lastYear" ? t("pivot_mode_compare") : t("pivot_mode_compare_previous"),
        columns: [rowDimLabel(currentReport, t), t("current_period"), compareLabel, "Δ%"],
        rows: sorted.map((m) => [m.label, m.current, m.compare, m.delta]),
        footer: [t("total"), round1(current), hasCompare ? round1(compare) : null, totalsDelta],
        freeze: { row: 1, col: 1 },
        percentCols: [3],
        boldLastCol: false,
    };
};

// Whether the payload carries comparison data for a given source (skip the sheet otherwise).
const hasComparison = (data, source) =>
    !!data?.comparisonPeriods?.[source] || (data?.rows || []).some((r) => r.comparison?.[source]);

// Build one sheet per AVAILABLE mode. Compare sheets need comparison data; hiding the mode toggle
// (pivotHidden MODE/SETTINGS) reduces the export to the normal sheet only.
export const buildSheetSpecs = (data, currentReport, t, hidden = []) => {
    if (!data || !(data.rows || []).length) return [];

    const specs = [buildNormalSheet(data, currentReport, t)];

    const modeLocked = hidden.includes(pivotSettings.MODE) || hidden.includes(pivotSettings.SETTINGS);
    if (modeLocked) return specs;

    if ((data.columns || []).length >= 2) {
        specs.push(buildPeriodSheet(data, currentReport, t));
    }
    if (hasComparison(data, "previous")) {
        specs.push(buildCompareSheet(data, currentReport, "previous", t("vs_prev_period"), t));
    }
    if (hasComparison(data, "lastYear")) {
        specs.push(buildCompareSheet(data, currentReport, "lastYear", t("vs_last_year"), t));
    }

    return specs;
};
